import { motion } from "framer-motion";
import { Wallet, ArrowRightLeft, Coins, Rocket, ExternalLink } from "lucide-react";

const UNISWAP_URL = "https://app.uniswap.org/swap?outputCurrency=0x770dfE19F79506B724D094ad4CAC6a5dBb21F513&chain=ethereum";

const steps = [
  {
    number: "01",
    title: "Get a Wallet",
    description: "Download MetaMask or your preferred Web3 wallet. This will be your portal to crypto domination.",
    icon: Wallet,
    link: "https://metamask.io/download/",
    linkText: "Get MetaMask",
  },
  {
    number: "02",
    title: "Buy ETH",
    description: "Fund your wallet with Ethereum (ETH). You'll need this to swap for $MOJO and pay gas fees.",
    icon: Coins,
    link: null,
    linkText: null,
  },
  {
    number: "03",
    title: "Go to Uniswap",
    description: "Connect your wallet to Uniswap and paste the $MOJO contract address to find the token.",
    icon: ArrowRightLeft,
    link: UNISWAP_URL,
    linkText: "Open Uniswap",
  },
  {
    number: "04",
    title: "Swap for $MOJO",
    description: "Enter the amount of ETH you want to swap, set slippage to 1-2%, and confirm the transaction.",
    icon: Rocket,
    link: null,
    linkText: null,
  },
];

const HowToBuy = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  return (
    <section id="how-to-buy" className="py-24 px-6 md:px-12 bg-[#0F0518]">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="text-[#9D4CDD] font-body text-xs tracking-[0.3em] uppercase mb-4">
            Join the Evil
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
            How to Buy <span className="text-[#FFD700]">$MOJO</span>
          </h2>
          <p className="text-[#A199AA] font-body mt-4 max-w-2xl mx-auto">
            Even the Powerpuff Girls couldn't stop you from following these simple steps
          </p>
        </motion.div>

        {/* Steps Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          data-testid="how-to-buy-steps"
        >
          {steps.map((step, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="bento-card group relative"
              data-testid={`step-${index + 1}`}
            >
              {/* Step Number */}
              <div className="step-number absolute top-4 right-4 select-none">
                {step.number}
              </div>

              {/* Content */}
              <div className="relative z-10 space-y-4">
                <div className="w-14 h-14 bg-[#FFD700]/10 border border-[#FFD700]/30 flex items-center justify-center group-hover:bg-[#FFD700]/20 transition-colors">
                  <step.icon size={28} className="text-[#FFD700]" />
                </div>

                <h3 className="font-heading text-xl font-bold text-white">
                  {step.title}
                </h3>

                <p className="text-[#A199AA] font-body text-sm leading-relaxed">
                  {step.description}
                </p>

                {step.link && (
                  <a
                    href={step.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-[#FFD700] text-sm font-medium hover:underline mt-2"
                  >
                    {step.linkText}
                    <ExternalLink size={14} />
                  </a>
                )}
              </div>

              {/* Connecting Line (hidden on last item) */}
              {index < steps.length - 1 && (
                <div className="hidden lg:block absolute top-1/2 -right-3 w-6 h-[2px] bg-gradient-to-r from-[#9D4CDD] to-transparent" />
              )}
            </motion.div>
          ))}
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="text-center mt-12"
        >
          <a
            href={UNISWAP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-[#FFD700] text-[#05000A] px-10 py-4 font-bold uppercase tracking-wide text-lg hover:bg-[#FFF066] hover:-translate-y-1 transition-all glow-yellow"
            data-testid="how-to-buy-cta"
          >
            Buy $MOJO Now
            <ExternalLink size={20} />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default HowToBuy;
